import React, { useEffect, useState } from 'react';
import { numberWithCommas } from '@components/common';
import DateRangePicker, { DateRangePreset } from '@components/common/DateRangePicker';
import Table, { TableHeaderContainerV2 } from '@components/common/Table';
import PaginationComponent from '@components/pagination';
import { paylogListColumns1 } from '@data/columns';
import { getPaylogList } from '@lib/api';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import { asArray } from '@util/array';
import Format from '@util/formatter';

interface PayTabProps {
  merchantId: number;
  phoneNum: string;
  setIsLoading: (loading: boolean) => void;
}

const PayTab = ({ merchantId, phoneNum, setIsLoading }: PayTabProps) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();

  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month').add(1, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [totalLength, setTotalLength] = useState(0);
  const [dataList, setDataList] = useState<any[]>([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const dateRange = Format.formatDateRange(startDate, endDate);
      const { data } = await createRequest(
        getPaylogList,
        merchantId,
        dateRange.start,
        dateRange.end,
        currentPage,
        postsPerPage,
        phoneNum,
      );
      const payRows = asArray<any>(data?.info);
      setDataList(
        payRows.map((item) => ({
          ...item,
          ticketType:
            item.type === 1
              ? ' 일회권'
              : item.type === 2
              ? ` 기간권 - ${item.seatType === 0 ? '자유석' : '고정석'}`
              : item.type === 3
              ? ' 시간정액권'
              : item.type === 4
              ? ' 세미나실'
              : item.type === 5
              ? '사물함'
              : item.meta8?.includes('자판기')
              ? '자판기'
              : item.meta8?.includes('컵코인')
              ? '컵코인'
              : item.meta8?.includes('백오피스')
              ? '백오피스'
              : item.meta8?.includes('이전 데이터')
              ? '이전 데이터'
              : '-',
          price: item.amount === 0 ? 0 : !item.amount ? '-' : numberWithCommas(item.amount),
          createdAt: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          cancelType: item.paymentType?.includes('cancel') ? (
            <span
              style={{
                textDecorationLine: 'line-through',
                color: '#E00F2C',
              }}>
              결제완료(환불완료)
            </span>
          ) : (
            <span>결제완료</span>
          ),
        })),
      );
      setTotalLength(Number(data?.count) || payRows.length);
    } catch (error) {
      catchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (merchantId && phoneNum) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, phoneNum, currentPage, startDate, endDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    else if (name === 'endDate') setEndDate(value);
    setCurrentPage(1);
  };

  return (
    <>
      <TableHeaderContainerV2>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          defaultPreset={DateRangePreset.PREV_MONTH}
          maxPeriod={DateRangePreset.YEAR}
        />
      </TableHeaderContainerV2>
      <div className="datatable-container">
        <Table tableHeight={500} data={dataList} columns={paylogListColumns1} striped={true} />
      </div>
      {totalLength > 0 && (
        <PaginationComponent
          setCurrentPage={setCurrentPage}
          totalLength={totalLength}
          postsPerPage={postsPerPage}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default PayTab;
